import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import ValidatorUtils from '@core/utils/validator.utils';
import {
  DvButtonComponent,
  DvDialogContent,
  DvDialogContentComponent,
} from '@devinco/ui/dds';
import {
  DvCheckboxElement,
  DvDynamicFormElement,
  DvDynamicFormModule,
  DvDynamicFormUtils,
  DvFormUtils,
  DvTextboxElement,
  FormOf,
} from '@devinco/ui/legacy';
import { DataFilterAction } from '@shared/constants/data-filter-action';
import { Entity } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { TranslateModule } from '@ngx-translate/core';
import { TripFilterDto } from '@pages/reports-page/interfaces';
import { TripCrudDialogData } from '@pages/trips-page/interfaces';
import { excludedRequestValues } from '@shared/components/edit-trip-filter-dialog/constants';
import { DataFilterCreateEditForm } from '@shared/components/edit-trip-filter-dialog/interfaces';
import { DataFilterElementsService } from '@shared/components/edit-trip-filter-dialog/services/data-filter-elements.service';
import { PipesModule } from '@shared/pipes/pipes.module';
import { switchMap, take, tap } from 'rxjs';

@Component({
  imports: [
    DvDynamicFormModule,
    PipesModule,
    TranslateModule,
    DvButtonComponent,
    DvDialogContentComponent,
  ],
  selector: 'app-trip-filter-crud-dialog',
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './trip-filter-crud-dialog.component.html',
})
export class TripFilterCrudDialogComponent
  extends DvDialogContent<TripCrudDialogData, TripFilterCrudDialogComponent>
  implements OnInit
{
  private api = inject(RestApiService);
  private dataFilterService = inject(DataFilterElementsService);
  private rolesAndPermissionsService = inject(UserRolesAndPermissionsService);

  elements: DvDynamicFormElement[];
  formGroup: FormGroup<
    FormOf<Pick<DataFilterCreateEditForm, 'Name' | 'VisibleToAllUsers'>>
  >;
  ngOnInit(): void {
    this.rolesAndPermissionsService
      .hasPermissions([RolesAndPermissions.FilterEditExtended])
      .pipe(take(1))
      .subscribe(hasPermission => {
        this.elements = this.getElements(hasPermission);
        this.formGroup = DvFormUtils.createFormGroup(this.elements, true);
        if (this.data.currentFilterName) {
          this.formGroup.setValue(
            {
              Name: this.data.currentFilterName,
              VisibleToAllUsers: this.data.visibleToAllUsers ?? false,
            },
            { emitEvent: false }
          );
        }
      });
  }

  getElements(hasPermission: boolean): DvDynamicFormElement[] {
    return [
      new DvTextboxElement({
        colspan: 12,
        key: 'Name',
        label: 'Report.FilterEdit.Name',
        validators: [Validators.required, ValidatorUtils.noWhitespaceValidator],
      }),
      new DvCheckboxElement({
        colspan: 12,
        checkboxLabel: 'Report.FilterEdit.VisibleToAllUsers',
        key: 'VisibleToAllUsers',
        disabled: !hasPermission,
      }),
    ];
  }

  cancel(): void {
    this.dialogRef.onClose();
  }

  performSave(): void {
    switch (this.data.dataFilterAction) {
      case DataFilterAction.Copy:
        this.copyDataFilter();
        break;
      case DataFilterAction.Edit:
        this.renameDataFilter();
        break;
      case DataFilterAction.Add:
        this.addDataFilter();
    }
  }

  private copyDataFilter(): void {
    const filterElements = [
      ...this.dataFilterService.getFilterNameElements(),
      ...this.dataFilterService.departmentAndDriverElements(),
      ...this.dataFilterService.getPeriodElements(),
      ...this.dataFilterService.getIntervalElements(),
      ...this.dataFilterService.getDistanceElements(),
    ];
    this.api
      .getInfo<TripFilterDto>(Entity.DataFilter, String(this.data.entityId), [
        ...DvDynamicFormUtils.getKeysForRequest(filterElements).filter(
          field => !excludedRequestValues.includes(field)
        ),
        'FilterModel',
      ])
      .pipe(
        switchMap(resp =>
          this.api
            .upsert(Entity.DataFilter, '0', {
              ...resp,
              ...this.formGroup.getRawValue(),
            })
            .pipe(tap(() => this.dialogRef!.onConfirm()))
        )
      )
      .pipe(take(1))
      .subscribe();
  }

  private renameDataFilter(): void {
    this.api
      .upsert(Entity.DataFilter, this.data.entityId, {
        name: this.formGroup.controls.Name!.value,
        visibleToAllUsers: this.formGroup.controls.VisibleToAllUsers!.value,
      })
      .pipe(take(1))
      .subscribe(() =>
        this.dialogRef!.onClose(this.formGroup.controls.Name!.value, 'close')
      );
  }

  private addDataFilter(): void {
    this.api
      .upsert(Entity.DataFilter, this.data.entityId, {
        name: this.formGroup.controls.Name!.value,
        visibleToAllUsers: this.formGroup.controls.VisibleToAllUsers!.value,
        ...this.data.addDataFilterPayload,
      })
      .pipe(take(1))
      .subscribe((id: number | undefined) =>
        this.dialogRef!.onClose(id, 'confirm')
      );
  }
}
